import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PasswordField } from '../../../components/PasswordField';
import { credentialErrorMessageKey } from '../../../lib/api';
import { useRequestEmailChangeMutation, type PartnerCockpitUser } from '../../../lib/auth';
import { SettingsCard } from './SettingsCard';

export type EmailCardProps = {
    user: Pick<PartnerCockpitUser, 'email' | 'hasPassword'>;
};

const EMPTY_FORM = { newEmail: '', currentPassword: '' };

export function EmailCard({ user }: EmailCardProps): ReactElement {
    const { t } = useTranslation();
    const requestEmailChange = useRequestEmailChangeMutation();
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitted, setSubmitted] = useState(false);

    const update = <K extends keyof typeof form>(key: K, next: (typeof form)[K]): void => {
        setForm((prev) => ({ ...prev, [key]: next }));
        setSubmitted(false);
    };

    const isDirty = form.newEmail.trim().length > 0 && form.currentPassword.length > 0;

    const handleSave = (): void => {
        requestEmailChange.mutate(
            { newEmail: form.newEmail.trim(), currentPassword: form.currentPassword },
            {
                onSuccess: () => {
                    setForm(EMPTY_FORM);
                    setSubmitted(true);
                },
            },
        );
    };

    const handleReset = (): void => {
        setForm(EMPTY_FORM);
        setSubmitted(false);
    };

    return (
        <SettingsCard
            title={t('SETTINGS.ACCOUNT.EMAIL_TITLE')}
            dirty={isDirty}
            saving={requestEmailChange.isPending}
            onSave={handleSave}
            onReset={handleReset}
            readOnly={!user.hasPassword}>
            <Stack spacing={3}>
                {user.hasPassword ? undefined : (
                    <Alert severity="info">{t('SETTINGS.ACCOUNT.EMAIL_PASSWORD_REQUIRED')}</Alert>
                )}
                <TextField
                    fullWidth
                    label={t('SETTINGS.ACCOUNT.CURRENT_EMAIL')}
                    type="email"
                    value={user.email}
                    disabled
                />
                {user.hasPassword ? (
                    <>
                        <TextField
                            fullWidth
                            label={t('SETTINGS.ACCOUNT.NEW_EMAIL')}
                            type="email"
                            autoComplete="email"
                            value={form.newEmail}
                            onChange={(e) => update('newEmail', e.target.value)}
                        />
                        <PasswordField
                            fullWidth
                            label={t('SETTINGS.ACCOUNT.CURRENT_PASSWORD')}
                            autoComplete="current-password"
                            value={form.currentPassword}
                            onChange={(e) => update('currentPassword', e.target.value)}
                            helperText={t('SETTINGS.ACCOUNT.EMAIL_REAUTH_HINT')}
                        />
                    </>
                ) : undefined}
                {submitted && requestEmailChange.isSuccess ? (
                    <Alert severity="success">{t('SETTINGS.ACCOUNT.EMAIL_CHANGE_PENDING')}</Alert>
                ) : undefined}
                {requestEmailChange.isError ? (
                    <Alert severity="error">
                        {t(credentialErrorMessageKey(requestEmailChange.error, 'SETTINGS.ACCOUNT.EMAIL_CHANGE_ERROR'))}
                    </Alert>
                ) : undefined}
            </Stack>
        </SettingsCard>
    );
}
